import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { generateParams } from '@/utils/functions/generateParams';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';

export type ITeamMember = {
  id: string | number;
  name: string;
  email: string;
  role?: string;
};

export type ITenant = {
  id: string | number;
  name: string;
};

interface TeamState {
  teamMembers: ITeamMember[];
  totalElements: number;
  totalPages: number;
  isLoading: boolean;
  error: unknown;

  fetchTenantTeam: (tenantId: string | number, paramsDataConfig: DataConfig) => Promise<void>;
  createInvitation: (tenantId: string | number, email: string, role: string) => Promise<void>;
  syncTeamMembers: (tenantId: string | number) => Promise<void>;
  updateRole: (tenantId: string | number, tenantUserId: string | number, role: string) => Promise<void>;
  revokeMember: (tenantId: string | number, tenantUserId: string | number) => Promise<void>;
}

const useTeamState = create<TeamState>((set, get) => ({
  teamMembers: [],
  totalElements: 0,
  totalPages: 1,
  isLoading: false,
  error: null,

  fetchTenantTeam: async (tenantId: string | number, paramsDataConfig: DataConfig) => {
    set({ isLoading: true, error: null });
    try {
      console.log('Fetching tenant team for tenantId:', tenantId, 'with params:', paramsDataConfig);
      const params = generateParams(paramsDataConfig);
      const url = new URL(env.apiTasklistBaseUrl + `/api/v1/tenant/tenants/${tenantId}/users${params}`);
      const { data } = await axiosInstance.get(url.toString());

      const rawData = data.data.content;
      let team: ITeamMember[];
      if (Array.isArray(rawData)) {
        team = rawData;
      } else if (rawData) {
        team = [rawData];
      } else {
        team = [];
      }
      set({
        teamMembers: team,
        totalElements: data.data.totalElements ?? team.length,
        totalPages: data.data.totalPages ?? 1,
        isLoading: false,
      });
    } catch (error) {
      set({ error, teamMembers: [], isLoading: false });
      console.error('Error fetching tenant team:', error);
    }
  },
  createInvitation: (tenantId: string | number, email: string, role: string) => {
    const url = new URL(env.apiTasklistBaseUrl + `/api/v1/tenant/tenants/${tenantId}/invite`);
    return axiosInstance
      .post(url.toString(), { email, role })
      .then(response => {
        toast.success('Invitation sent successfully');
        get().fetchTenantTeam(tenantId, { page: 0, limit: 10 });
      })
      .catch(error => {
        errorHandler(error);
      });
  },
  syncTeamMembers: async (tenantId: string | number) => {
    try {
      const url = new URL(env.apiTasklistBaseUrl + `/api/v1/authentication/sync/users`);
      await axiosInstance.post(url.toString(), { target_tenant: Number(tenantId) });
      toast.success('Team members synchronized successfully');
      await get().fetchTenantTeam(tenantId, { page: 0, limit: 10 });
    } catch (error) {
      errorHandler(error);
    }
  },

  updateRole: (tenantId: string | number, tenantUserId: string | number, role: string) => {
    const url = new URL(env.apiTasklistBaseUrl + `/api/v1/tenant/tenants/${tenantId}/users/update`);
    return axiosInstance
      .post(url.toString(), { tenant_user_id: tenantUserId, role })
      .then(() => {
        toast.success('Role updated successfully');
        get().fetchTenantTeam(tenantId, { page: 0, limit: 10 });
      })
      .catch(error => {
        errorHandler(error);
      });
  },

  revokeMember: (tenantId: string | number, tenantUserId: string | number) => {
    const url = new URL(env.apiTasklistBaseUrl + `/api/v1/tenant/tenants/${tenantId}/users/revoke`);
    return axiosInstance
      .post(url.toString(), { tenant_user_id: tenantUserId })
      .then(() => {
        toast.success('Member revoked successfully');
        get().fetchTenantTeam(tenantId, { page: 0, limit: 10 });
      })
      .catch(error => {
        errorHandler(error);
      });
  },
}));

export default useTeamState;
